import { useContext } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import _ from "lodash";
import cx from "classnames";

import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";

import { Asset } from "src/types/assets";
import { DealRoomActionPlanContext } from "../../../../context";
import { assetMetadata } from "src/utils/dealRoom.helper";

import styles from "./styles.module.scss";

const AssetCard = ({ asset, onView }) => {
  const handleClick = () => {
    if (onView) {
      onView(asset);
    }
  };

  return (
    <div
      className={cx(styles.entity, styles.asset)}
      style={{ width: 230 }}
      onClick={handleClick}
    >
      <div className={styles.inner}>
        <Thumbnail
          className={styles.icon}
          media={asset?.icon}
          width={44}
          height={40}
          defaultImage="/images/assets/default_asset.jpg"
        />
        <div className={styles.details}>
          <Text
            className={styles.name}
            text={_.get(asset, "name") ?? "N/A"}
            tooltip={{ placement: "right" }}
          />
          <div className={styles.meta}>
            <p>{assetMetadata(asset)}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <i className={cx(styles.viewIcon, "paperflite-redirect")} />
        </div>
      </div>
    </div>
  );
};

interface AssetsPopoverProps {
  stageId: string;
  actionId: string;
  selectedAssets: Asset[];
  isNewAction: boolean;
  disabled: boolean;
  canEditAction: boolean;
  isActionCompleted: boolean;
}

const getButtonText = (assets: Asset[]) => {
  const numberOfAttachments = _.size(assets);

  if (numberOfAttachments === 0) {
    return "Attach";
  }

  return `${numberOfAttachments} Attachment${numberOfAttachments > 1 ? "s" : ""}`;
};

const AssetsPopover = ({
  stageId,
  actionId,
  selectedAssets,
  isNewAction,
  disabled,
  canEditAction,
  isActionCompleted,
}: AssetsPopoverProps) => {
  const { state, toggleEditAction, toggleAddAssetsSlider } = useContext(
    DealRoomActionPlanContext,
  );

  const editMode = _.get(state, "editMode");
  const isControlDisabled = disabled || !canEditAction || isActionCompleted;

  const buttonText = getButtonText(selectedAssets);

  const handleOpenAssetsSlider = () => {
    if (!editMode || isControlDisabled) {
      return;
    }

    toggleEditAction(stageId, actionId, isActionCompleted);
    document.body.click();
    toggleAddAssetsSlider(true, stageId, actionId, isNewAction, selectedAssets);
  };

  const handleViewAsset = (asset: Asset) => {
    window.open(`/assets/${asset.id}`, "_blank");
  };

  const hasAssets = _.size(selectedAssets) > 0;

  const actionButton = (
    <div
      className={cx(styles.actionButton, {
        [styles.disabled]:
          !canEditAction ||
          isActionCompleted
      })}
      onClick={hasAssets ? undefined : handleOpenAssetsSlider}
    >
      <i className={cx(styles.icon, "paperflite-attachment")} />
      <p className={styles.buttonText}>{buttonText}</p>
    </div>
  );

  if (!hasAssets) {
    return actionButton;
  }

  return (
    <OverlayTrigger
      trigger="click"
      placement="left"
      flip
      rootClose
      container={document.body}
      overlay={
        <Popover id={`popover-${actionId}`} className={styles.popover}>
          <Popover.Body>
            <div className={styles.container}>
              <div className={styles.headingContainer}>
                <p className={styles.heading}>Attached documents</p>
                {(editMode && !isActionCompleted) && (
                  <div
                    className={styles.addAssetsContainer}
                    onClick={handleOpenAssetsSlider}
                  >
                    <i className={cx(styles.icon, "paperflite-edit")} />
                    <p className={styles.text}>Edit</p>
                  </div>
                )}
              </div>
              <div className={styles.assetsContainer}>
                {_.map(selectedAssets, (asset: Asset, index: number) => (
                  <AssetCard
                    asset={asset}
                    onView={handleViewAsset}
                    key={`viewAssetCard-${index}`}
                  />
                ))}
              </div>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {actionButton}
    </OverlayTrigger>
  );
};

export default AssetsPopover;
